import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { InstantSearch, Configure } from 'react-instantsearch'
import React, { PropsWithChildren, useMemo } from 'react'

// InstantSearch issues a search as soon as it mounts, even with an empty query.
// SearchProvider is mounted in the primary navigation on every page, so every
// page view was costing an Algolia search operation that returned results no
// one could see: 67,498 of 69,141 logged searches over 30 days (97.6%) had an
// empty query. On a pay-as-you-go plan those are billed like any other search.
//
// Short-circuit them client-side. Returning a well-formed empty response keeps
// InstantSearch's state machine happy without a network round trip.
const emptyResponse = (indexName: string) => ({
  hits: [],
  nbHits: 0,
  nbPages: 0,
  page: 0,
  hitsPerPage: 0,
  processingTimeMS: 0,
  exhaustiveNbHits: true,
  query: '',
  params: '',
  index: indexName,
})

export const SearchProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const indexName = process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME || 'production_crawler_pages'

  const searchClient = useMemo(() => {
    const client = algoliasearch(
      process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || 'PGYY5JY8KO',
      process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || 'ad1cc03028d160958e8254786540b76c',
    )

    return {
      ...client,
      search(requests) {
        // InstantSearch passes an array; the raw client also accepts { requests }.
        const list = Array.isArray(requests) ? requests : requests?.requests ?? []

        if (list.length > 0 && list.every(request => !request?.params?.query)) {
          return Promise.resolve({
            results: list.map(request => emptyResponse(request?.indexName ?? indexName)),
          })
        }

        return client.search(requests)
      },
    }
  }, [indexName])

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
    >
      <Configure hitsPerPage={20} />
      {children}
    </InstantSearch>
  )
}
